import React, { useState } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Box,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { logo } from "../../assets";
import { login_api, google_signin_admin_api } from "../../DAL/Login/Login";
import { useContentSetting } from "../../Hooks/ContentSetting";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const keyframes = `
  @keyframes floatOrb1 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50% { transform: translate(40px, -30px) scale(1.08); }
  }
  @keyframes floatOrb2 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50% { transform: translate(-50px, 40px) scale(1.12); }
  }
  @keyframes floatOrb3 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50% { transform: translate(30px, 30px) scale(0.94); }
  }
  @keyframes cardIn {
    from { opacity: 0; transform: translateY(18px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes logoGlow {
    0%, 100% { filter: drop-shadow(0 0 10px rgba(255,255,255,0.25)); }
    50% { filter: drop-shadow(0 0 22px rgba(255,255,255,0.5)); }
  }
`;

export default function Login() {
  const { setUserInfo }     = useContentSetting();
  const { enqueueSnackbar } = useSnackbar();
  const navigate            = useNavigate();
  const [isLoading, setIsLoading]   = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [state, setState] = useState({ email: "", password: "" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState((prev) => ({ ...prev, [name]: value }));
  };

  const handleAuthSuccess = (result) => {
    setUserInfo(result.adminUser);
    localStorage.setItem("user_data", JSON.stringify(result.adminUser));
    localStorage.setItem("token", result.token);
    if (result.adminUser?.type === "school_admin" || result.adminUser?.flightSchool) {
      navigate("/school/dashboard");
    } else {
      navigate("/flight-schools");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const result = await login_api({
      email: state.email,
      password: state.password,
      loginType: "school_admin",
    });

    if (result.code === 200) {
      handleAuthSuccess(result);
    } else if (result.code === 403 && result.requiresActivation) {
      enqueueSnackbar("Please activate your account first", { variant: "warning" });
      sessionStorage.setItem("pending_activation", JSON.stringify({
        token: result.activationToken,
        schoolId: result.schoolId,
      }));
      navigate("/activate");
    } else {
      enqueueSnackbar(result.message || "Invalid credentials", { variant: "error" });
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setGoogleLoading(true);
    try {
      const result = await google_signin_admin_api(credentialResponse.credential);
      if (result.code === 200) {
        handleAuthSuccess(result);
      } else {
        enqueueSnackbar(result.message || "Google sign-in failed", { variant: "error" });
      }
    } catch (err) {
      enqueueSnackbar("Google sign-in failed", { variant: "error" });
    } finally {
      setGoogleLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.06)",
      borderRadius: "12px",
      color: "#fff",
      fontSize: "14px",
      fontFamily: "'Rubik', sans-serif",
      transition: "all 0.15s ease",
      "& fieldset": { borderColor: "rgba(255,255,255,0.16)" },
      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.3)" },
      "&.Mui-focused fieldset": { borderColor: "#c01e2e", borderWidth: "1.5px" },
      "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(192,30,46,0.25)" },
    },
    "& .MuiInputBase-input": { color: "#fff" },
    "& .MuiInputLabel-root": {
      color: "rgba(255,255,255,0.5)",
      fontSize: "14px",
      fontFamily: "'Rubik', sans-serif",
    },
    "& .MuiInputLabel-root.Mui-focused": { color: "#ff6b7a" },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "radial-gradient(1200px 700px at 15% 0%, rgba(192,30,46,0.35), transparent 60%), radial-gradient(1000px 600px at 90% 100%, rgba(30,58,95,0.5), transparent 55%), linear-gradient(160deg, #0d0d0f 0%, #1a1a1d 55%, #14141f 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Rubik', sans-serif",
        position: "relative",
        overflow: "hidden",
        p: 2,
      }}
    >
      <style>{keyframes}</style>

      {/* Floating gradient orbs */}
      <Box sx={{
        position: "absolute", top: "8%", left: "8%", width: 340, height: 340, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(192,30,46,0.45), transparent 70%)",
        filter: "blur(10px)", animation: "floatOrb1 12s ease-in-out infinite", pointerEvents: "none",
      }} />
      <Box sx={{
        position: "absolute", bottom: "5%", right: "10%", width: 420, height: 420, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(30,58,95,0.55), transparent 70%)",
        filter: "blur(10px)", animation: "floatOrb2 14s ease-in-out infinite", pointerEvents: "none",
      }} />
      <Box sx={{
        position: "absolute", top: "45%", right: "22%", width: 220, height: 220, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(255,193,7,0.22), transparent 70%)",
        filter: "blur(10px)", animation: "floatOrb3 10s ease-in-out infinite", pointerEvents: "none",
      }} />

      {/* Card */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
          mx: 2,
          position: "relative",
          backgroundColor: "rgba(255,255,255,0.05)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: "22px",
          boxShadow: "0 30px 70px -20px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.08)",
          padding: "44px 36px 36px",
          animation: "cardIn 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* Logo */}
        <Box sx={{ mb: 3.5, textAlign: "center" }}>
          <img
            src={logo}
            alt="XAir"
            style={{ height: 32, width: "auto", animation: "logoGlow 3s ease-in-out infinite" }}
          />
        </Box>

        <Typography
          sx={{
            fontSize: "22px",
            fontWeight: 800,
            color: "#fff",
            fontFamily: "'Rubik', sans-serif",
            mb: 0.5,
            textAlign: "center",
            letterSpacing: "-0.4px",
          }}
        >
          Welcome back
        </Typography>
        <Typography
          sx={{
            fontSize: "13px",
            color: "rgba(255,255,255,0.5)",
            fontFamily: "'Rubik', sans-serif",
            mb: 3.5,
            textAlign: "center",
          }}
        >
          Sign in to the XAir Operations Portal
        </Typography>

        {/* Google Sign-In */}
        {GOOGLE_CLIENT_ID && (
          <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <Box sx={{
              display: "flex", justifyContent: "center", mb: 2.5, opacity: googleLoading ? 0.6 : 1,
              pointerEvents: googleLoading ? "none" : "auto",
              "& > div": { width: "100% !important" },
              "& iframe": { width: "100% !important" },
            }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => enqueueSnackbar("Google sign-in failed", { variant: "error" })}
                theme="filled_black"
                shape="pill"
                size="large"
                width="328"
                text="continue_with"
              />
            </Box>
          </GoogleOAuthProvider>
        )}

        {GOOGLE_CLIENT_ID && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{ flex: 1, height: "1px", backgroundColor: "rgba(255,255,255,0.12)" }} />
            <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.4)", fontFamily: "'Rubik', sans-serif" }}>
              or with email
            </Typography>
            <Box sx={{ flex: 1, height: "1px", backgroundColor: "rgba(255,255,255,0.12)" }} />
          </Box>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Email"
              type="email"
              name="email"
              value={state.email}
              onChange={handleChange}
              required
              fullWidth
              size="small"
              sx={inputSx}
            />

            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              name="password"
              value={state.password}
              onChange={handleChange}
              required
              fullWidth
              size="small"
              sx={inputSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      sx={{ color: "rgba(255,255,255,0.4)" }}
                    >
                      {showPassword
                        ? <VisibilityOff sx={{ fontSize: 16 }} />
                        : <Visibility   sx={{ fontSize: 16 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <button
              type="submit"
              disabled={isLoading}
              style={{
                marginTop: "4px",
                width: "100%",
                padding: "13px",
                background: isLoading
                  ? "rgba(192,30,46,0.5)"
                  : "linear-gradient(135deg, #c01e2e 0%, #a81828 100%)",
                color: "#fff",
                border: "none",
                borderRadius: "999px",
                fontSize: "14px",
                fontWeight: 700,
                cursor: isLoading ? "not-allowed" : "pointer",
                transition: "transform 0.18s cubic-bezier(0.4,0,0.2,1), box-shadow 0.18s cubic-bezier(0.4,0,0.2,1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                fontFamily: "'Rubik', sans-serif",
                boxShadow: "0 6px 20px rgba(192,30,46,0.4), inset 0 1px 0 rgba(255,255,255,0.2)",
              }}
              onMouseEnter={(e) => { if (!isLoading) { e.currentTarget.style.transform = "translateY(-2px) scale(1.01)"; e.currentTarget.style.boxShadow = "0 10px 28px rgba(192,30,46,0.5), inset 0 1px 0 rgba(255,255,255,0.25)"; } }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0) scale(1)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(192,30,46,0.4), inset 0 1px 0 rgba(255,255,255,0.2)"; }}
            >
              {isLoading
                ? <CircularProgress size={16} sx={{ color: "#fff" }} />
                : "Continue"
              }
            </button>
          </Box>
        </form>

        <Typography
          sx={{
            textAlign: "center",
            mt: 2,
          }}
        >
          <RouterLink
            to="/forgot-password"
            style={{
              color: "rgba(255,255,255,0.55)",
              fontSize: "12px",
              fontFamily: "'Rubik', sans-serif",
              textDecoration: "none",
            }}
          >
            Forgot your password?
          </RouterLink>
        </Typography>

        <Typography
          sx={{
            textAlign: "center",
            color: "rgba(255,255,255,0.45)",
            fontSize: "12px",
            fontFamily: "'Rubik', sans-serif",
            mt: 3,
          }}
        >
          Are you an independent instructor?{" "}
          <RouterLink to="/instructors/signup" style={{ color: "#ff6b7a", fontWeight: 600, textDecoration: "none" }}>
            Create your group
          </RouterLink>
        </Typography>

        <Typography
          sx={{
            textAlign: "center",
            color: "rgba(255,255,255,0.3)",
            fontSize: "11px",
            fontFamily: "'Rubik', sans-serif",
            mt: 2,
          }}
        >
          XAir Operations Portal
        </Typography>
      </Box>
    </Box>
  );
}
